import React, { useState } from 'react';
import {
  Paintbrush,
  Sliders,
  Layers,
  Trash2,
  Box,
} from 'lucide-react';
import { useBreakpoint } from '@shared/hooks/use-breakpoint';
import { BottomSheet } from '@shared/ui/adaptive';
import type { BlockNode, ViewportDevice, StyleTargetState } from '../types';
import { StyleManagerPanel } from './StyleManagerPanel';
import { TraitsManagerPanel } from './TraitsManagerPanel';
import { LayerTreePanel } from './LayerTreePanel';

export type RightDockTab = 'style' | 'traits' | 'layers';

export interface PropertyInspectorProps {
  selectedNode: BlockNode | null;
  rootNode?: BlockNode;
  activeTab?: RightDockTab;
  onTabChange?: (tab: RightDockTab) => void;
  activeBreakpoint?: ViewportDevice;
  activeState?: StyleTargetState;
  onStateChange?: (state: StyleTargetState) => void;
  onUpdateProps: (nodeId: string, props: Record<string, unknown>) => void;
  onUpdateStylesTarget?: (
    nodeId: string,
    patch: Record<string, string | undefined>,
    target?: { breakpoint?: ViewportDevice; state?: StyleTargetState }
  ) => void;
  onUpdateStyles?: (nodeId: string, styles: Record<string, string>) => void;
  onUpdateMeta?: (
    nodeId: string,
    meta: Partial<Pick<BlockNode, 'customName' | 'htmlId' | 'classes' | 'hidden' | 'locked' | 'visibility' | 'slotBinding'>>
  ) => void;
  onDeleteNode: (nodeId: string) => void;
  onSelectNode?: (id: string) => void;
  onToggleHide?: (id: string, hidden: boolean) => void;
  onToggleLock?: (id: string, locked: boolean) => void;
  onHoverBoxPart?: (part: 'margin' | 'padding' | null) => void;
  isOpenMobile: boolean;
  onCloseMobile: () => void;
  pages?: Array<{ id: string; title: string; route_path: string }>;
}

export function PropertyInspector({
  selectedNode,
  rootNode,
  activeTab: externalTab,
  onTabChange: externalOnTabChange,
  activeBreakpoint = 'desktop',
  activeState = 'default',
  onStateChange,
  onUpdateProps,
  onUpdateStylesTarget,
  onUpdateStyles,
  onUpdateMeta,
  onDeleteNode,
  onSelectNode,
  onToggleHide,
  onToggleLock,
  onHoverBoxPart,
  isOpenMobile,
  onCloseMobile,
  pages = [],
}: PropertyInspectorProps): React.JSX.Element {
  const { isDesktop } = useBreakpoint();
  const [internalTab, setInternalTab] = useState<RightDockTab>('style');

  const currentTab = externalTab ?? internalTab;
  const setTab = externalOnTabChange ?? setInternalTab;

  const handleUpdateStyles = (
    nodeId: string,
    patch: Record<string, string | undefined>,
    target?: { breakpoint?: ViewportDevice; state?: StyleTargetState }
  ) => {
    if (onUpdateStylesTarget) {
      onUpdateStylesTarget(nodeId, patch, target);
    } else if (onUpdateStyles) {
      const cleanPatch: Record<string, string> = {};
      for (const [k, v] of Object.entries(patch)) {
        if (v !== undefined) cleanPatch[k] = v;
      }
      onUpdateStyles(nodeId, cleanPatch);
    }
  };

  const renderContent = () => {
    if (currentTab === 'layers' && rootNode) {
      return (
        <LayerTreePanel
          root={rootNode}
          selectedId={selectedNode?.id || null}
          onSelectNode={(id) => onSelectNode && onSelectNode(id)}
          onToggleHide={(id, hidden) => onToggleHide && onToggleHide(id, hidden)}
          onToggleLock={(id, locked) => onToggleLock && onToggleLock(id, locked)}
        />
      );
    }

    if (!selectedNode) {
      return (
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center text-muted-foreground select-none">
          <Box className="h-8 w-8 mb-2 opacity-40 text-primary" />
          <div className="text-xs font-semibold text-foreground">No Element Selected</div>
          <p className="text-[11px] text-muted-foreground mt-1 max-w-[200px]">
            Click any block on the canvas to inspect its styles, traits, and layout.
          </p>
        </div>
      );
    }

    if (currentTab === 'traits') {
      return (
        <TraitsManagerPanel
          selectedNode={selectedNode}
          onUpdateProps={onUpdateProps}
          onUpdateMeta={onUpdateMeta}
          pages={pages}
        />
      );
    }

    // Default: Style Manager
    return (
      <StyleManagerPanel
        selectedNode={selectedNode}
        activeBreakpoint={activeBreakpoint}
        activeState={activeState}
        onStateChange={onStateChange}
        onUpdateStylesTarget={handleUpdateStyles}
        onHoverBoxPart={onHoverBoxPart}
      />
    );
  };

  const rightDockHeader = (
    <div className="flex items-center justify-between border-b border-border bg-muted/40 p-1 shrink-0 select-none">
      {/* 3 Iconic GrapesJS Tabs - High Density */}
      <div className="flex items-center gap-0.5 bg-muted/60 p-0.5 rounded border border-border">
        <button
          type="button"
          onClick={() => setTab('style')}
          className={`flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold transition-colors cursor-pointer ${
            currentTab === 'style'
              ? 'bg-background text-primary shadow-xs'
              : 'text-muted-foreground hover:text-foreground'
          }`}
          title="Style Manager (6 CSS Sectors)"
        >
          <Paintbrush className="h-3 w-3" />
          <span>Style</span>
        </button>

        <button
          type="button"
          onClick={() => setTab('traits')}
          className={`flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold transition-colors cursor-pointer ${
            currentTab === 'traits'
              ? 'bg-background text-primary shadow-xs'
              : 'text-muted-foreground hover:text-foreground'
          }`}
          title="Component Settings & Traits"
        >
          <Sliders className="h-3 w-3" />
          <span>Traits</span>
        </button>

        {rootNode && (
          <button
            type="button"
            onClick={() => setTab('layers')}
            className={`flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-bold transition-colors cursor-pointer ${
              currentTab === 'layers'
                ? 'bg-background text-primary shadow-xs'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            title="Layer Navigator"
          >
            <Layers className="h-3 w-3" />
            <span>Layers</span>
          </button>
        )}
      </div>

      {selectedNode && (
        <button
          type="button"
          onClick={() => onDeleteNode(selectedNode.id)}
          className="p-1 text-muted-foreground hover:text-rose-500 hover:bg-rose-500/10 rounded transition-colors cursor-pointer"
          title="Delete Block"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      )}
    </div>
  );

  // Desktop Side Dock
  if (isDesktop) {
    return (
      <aside className="w-64 shrink-0 border-l border-border bg-card flex flex-col h-full overflow-hidden z-20">
        {rightDockHeader}
        <div className="flex-1 overflow-hidden">{renderContent()}</div>
      </aside>
    );
  }

  // Mobile Bottom Sheet
  return (
    <BottomSheet
      open={isOpenMobile}
      onOpenChange={(open) => {
        if (!open) onCloseMobile();
      }}
      title={selectedNode ? `${selectedNode.type} Settings` : 'Inspector'}
    >
      <div className="flex flex-col h-[75vh]">
        {rightDockHeader}
        <div className="flex-1 overflow-hidden">{renderContent()}</div>
      </div>
    </BottomSheet>
  );
}
